import { ReactElement, useState } from "react"
import { Formik, FormikValues } from "formik"
import * as Yup from "yup"
import { useQueryClient } from "@tanstack/react-query"
import { DialogDisclosure, useDialogState } from "reakit/Dialog"

import { Modal } from "../../components/Modal"
import { SubmitButton } from "../../components/SubmitButton"
import UserFormComponent from "./UserFormComponent"
import {
  useAccountId,
  useAddAccountUser,
  useUpdateAccountUser,
  useRemoveAccountUser,
} from "../../hooks"
import { RowDataTypes } from "@/components/Table/types"
import { AccountUser } from "@/types/accountUsers"

export interface UserFormProps {
  initialValues: any
  onSuccess: (action: string, user?: RowDataTypes) => void
  onError: (error: Error) => void
  backLink: ReactElement<any, any>
  deleteDisabled: boolean
  isNew: boolean
  submitText: string
  updateDisabled: boolean
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please provide a valid email address.")
    .required("Please provide an email address"),
  role: Yup.string().required("Please select a role"),
})

const UserForm = ({
  initialValues,
  onSuccess,
  onError,
  backLink,
  deleteDisabled,
  isNew,
  submitText,
  updateDisabled,
}: UserFormProps) => {
  const accountId = useAccountId()
  const queryClient = useQueryClient()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRemovingUser, setIsRemovingUser] = useState<boolean>(false)
  const removeUserDialog = useDialogState({ animated: true })

  const useUpsertAccountUser = isNew
    ? useAddAccountUser.bind(this, queryClient, accountId)
    : useUpdateAccountUser.bind(
        this,
        queryClient,
        accountId,
        initialValues.email as string
      )

  const { mutateAsync: upsertAccountUser } = useUpsertAccountUser({
    onSuccess: (user: AccountUser) => {
      if (user) {
        onSuccess("save", {
          id: user.email ?? "",
          email: user.email ?? "",
          first_name: user.first_name,
          last_name: user.last_name,
          is_requester: user.is_requester ?? false,
          role: user.role ?? "viewer",
        })
      }
    },
    onError: (error) => onError(error as Error),
  })

  const handleUpsertSubmit = async (values: FormikValues) => {
    setErrorMessage(null)
    await upsertAccountUser(values)
  }

  const { mutateAsync: removeAccountUser } = useRemoveAccountUser(
    queryClient,
    accountId,
    initialValues.email as string,
    {
      onSuccess: () => onSuccess("remove"),
      onError: (error) => onError(error as Error),
      onSettled: () => setIsRemovingUser(false),
    }
  )

  const handleRemoveSubmit = async () => {
    setIsRemovingUser(true)

    await removeAccountUser({})
  }

  return (
    <Formik<RowDataTypes>
      enableReinitialize={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={true}
      validateOnChange={true}
      onSubmit={handleUpsertSubmit}
    >
      {(formikProps) => (
        <>
          <UserFormComponent
            className="mb-0 md:order-2 md:w-full"
            errorMessage={errorMessage}
            formikProps={formikProps}
            removeUserDialog={removeUserDialog}
            backLink={backLink}
            deleteDisabled={deleteDisabled}
            isNew={isNew}
            submitText={submitText}
            updateDisabled={updateDisabled}
          />

          <Modal
            className="px-6 pt-6 pb-0 max-w-[1280px] overflow-visible"
            header="Confirm Remove User"
            aria-label="Confirm Remove User"
            dialog={removeUserDialog}
          >
            <div className="mb-6">
              <div className="mb-6" data-testid="remove-user-confirmation">
                Are you sure you'd like to remove{" "}
                <strong>{initialValues.email}</strong> from this account?
              </div>

              <div>
                <DialogDisclosure
                  {...removeUserDialog}
                  className="mb-2 mr-2 btn2 btn2-outline-primary font-semibold"
                  data-testid="cancel-remove-user"
                >
                  Cancel
                </DialogDisclosure>
                <SubmitButton
                  onClick={() => {
                    handleRemoveSubmit()
                  }}
                  isSubmitting={isRemovingUser}
                  className="mb-2 btn2 btn2-error font-semibold"
                  data-testid="confirm-remove-user"
                  typeButton
                >
                  Remove User
                </SubmitButton>
              </div>
            </div>
          </Modal>
        </>
      )}
    </Formik>
  )
}

export default UserForm
